import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteIcon from "@mui/icons-material/Delete";
import { formatCurrencyNew } from "utils/helper";

// One addon line item: name + unit price on the left, a quantity stepper and the
// live running total (unit price x quantity) on the right, so bumping +/- visibly
// changes the number instead of only the badge looking the same as before.
// Read-only (canEdit=false) is used for guest carts before addon editing existed
// and for any other place that just needs to display, not edit, an addon.
const AddonChip = ({ addon, currencySymbol, canEdit, onIncrease, onDecrease, onRemove }) => {
  // Prefer the backend-computed subtotal (price * qty + vat * qty) so the total
  // scales correctly as quantity changes; fall back to a client-side estimate
  // (used for guest carts, which have no subtotal_price from the API — price and
  // vatAmount are both per-unit there, so both get multiplied by quantity).
  const lineTotal =
    addon.subtotalPrice != null
      ? addon.subtotalPrice
      : ((addon.price || 0) + (addon.vatAmount || 0)) * (addon.quantity || 1);

  if (!canEdit) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          py: "7px",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: "var(--fs-xs)", color: "#374151" }}>
            {addon.name}{" "}
            <span style={{ color: "#9ca3af" }}>x{addon.quantity}</span>
          </Typography>
          {addon.vatAmount > 0 && (
            <Typography sx={{ fontSize: "10px", color: "#9ca3af" }}>
              VAT: {formatCurrencyNew(addon.vatAmount, currencySymbol)}
            </Typography>
          )}
        </Box>
        <Typography sx={{ fontSize: "var(--fs-xs)", fontWeight: 700, color: "#111" }}>
          {formatCurrencyNew(lineTotal, currencySymbol)}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        py: "8px",
      }}
    >
      {/* flex-basis 0 so this side never dictates whether the row needs to wrap —
          it always takes exactly the space left over after the fixed-width
          controls on the right, truncating its own text with ellipsis if tight. */}
      <Box sx={{ minWidth: 0, flex: "1 1 0%" }}>
        <Typography
          sx={{ fontSize: "var(--fs-xs)", fontWeight: 600, color: "#111827", lineHeight: 1.3 }}
          noWrap
        >
          {addon.name}
        </Typography>
        <Typography sx={{ fontSize: "10px", color: "#9ca3af", lineHeight: 1.2 }} noWrap>
          {formatCurrencyNew(addon.price, currencySymbol)} each
        </Typography>
        {addon.vatAmount > 0 && (
          <Typography sx={{ fontSize: "10px", color: "#9ca3af", lineHeight: 1.2 }} noWrap>
            VAT: {formatCurrencyNew(addon.vatAmount, currencySymbol)}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            border: "1px solid #d1d5db",
            borderRadius: "6px",
            backgroundColor: "#fff",
            overflow: "hidden",
          }}
        >
          <IconButton
            size="small"
            onClick={onDecrease}
            sx={{ p: "4px", borderRadius: 0 }}
            aria-label="decrease addon quantity"
          >
            <RemoveIcon sx={{ fontSize: 13 }} />
          </IconButton>
          <Typography
            sx={{ fontSize: "var(--fs-xs)", fontWeight: 600, width: "16px", textAlign: "center" }}
          >
            {addon.quantity}
          </Typography>
          <IconButton
            size="small"
            onClick={onIncrease}
            sx={{ p: "4px", borderRadius: 0 }}
            aria-label="increase addon quantity"
          >
            <AddIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </Box>

        <Typography
          sx={{
            fontSize: "var(--fs-xs)",
            fontWeight: 700,
            color: "#111",
            minWidth: "72px",
            flexShrink: 0,
            textAlign: "right",
            whiteSpace: "nowrap",
          }}
        >
          {formatCurrencyNew(lineTotal, currencySymbol)}
        </Typography>

        <IconButton
          size="small"
          onClick={onRemove}
          sx={{ p: "2px", color: "#9ca3af", "&:hover": { color: "#ef4444", background: "transparent" } }}
          aria-label="remove addon"
        >
          <DeleteIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default AddonChip;
